import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ThumbnailItem, ThumbnailItemState} from '@Components/thumbnail-item';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {FoldType} from '@PosterWhiteboard/poster/fold.class';
import {ContextualMenu} from '@Components/contextual-menu';
import styles from './poster-editing-folds-panel.module.scss';
import {useAppSelector} from '@/hooks';

interface PosterEditingFoldsPanelProps {
  panelId: string;
}

const MOBILE_FOLD_TYPES_TO_SHOW: FoldType[] = [
  FoldType.NONE,
  FoldType.BI_FOLD_HORIZONTAL,
  FoldType.BI_FOLD_VERTICAL,
  FoldType.TRI_FOLD_HORIZONTAL,
  FoldType.TRI_FOLD_VERTICAL,
  FoldType.FOUR_PANEL_HORIZONTAL,
  FoldType.FOUR_PANEL_VERTICAL,
];

function PosterEditingFoldsPanel({...props}: PosterEditingFoldsPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const foldType = useAppSelector((state) => {
    return state.posterEditor.posterObject?.fold.type;
  });
  if (foldType === undefined) {
    return null;
  }

  const applyFold = (foldToApply: FoldType): void => {
    if (foldType === foldToApply) {
      return;
    }
    void window.posterEditor?.whiteboard?.updateFromObject(
      {
        fold: {
          type: foldToApply,
        },
      },
      {
        undoable: false,
      }
    );
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_folds')} />;
  };

  const getNameFromType = (id: FoldType): string => {
    switch (id) {
      case FoldType.NONE:
        return window.i18next.t('pmwjs_none');
      case FoldType.BI_FOLD_HORIZONTAL:
        return window.i18next.t('pmwjs_bi_fold_horizontal');
      case FoldType.BI_FOLD_VERTICAL:
        return window.i18next.t('pmwjs_bi_fold_vertical');
      case FoldType.FOUR_PANEL_HORIZONTAL:
        return window.i18next.t('pmwjs_four_panel_horizontal');
      case FoldType.FOUR_PANEL_VERTICAL:
        return window.i18next.t('pmwjs_four_panel_vertical');
      case FoldType.TRI_FOLD_HORIZONTAL:
        return window.i18next.t('pmwjs_tri_fold_horizontal');
      case FoldType.TRI_FOLD_VERTICAL:
        return window.i18next.t('pmwjs_tri_fold_vertical');
      default:
        throw new Error(`Unhandled page fold dropdown item`);
    }
  };

  const getIconFromType = (id: FoldType): string => {
    switch (id) {
      case FoldType.NONE:
        return 'icon-ban';
      case FoldType.BI_FOLD_HORIZONTAL:
        return 'icon-bi-fold-h';
      case FoldType.BI_FOLD_VERTICAL:
        return 'icon-bi-fold-v';
      case FoldType.FOUR_PANEL_HORIZONTAL:
        return 'icon-quad-fold-h';
      case FoldType.FOUR_PANEL_VERTICAL:
        return 'icon-quad-fold-v';
      case FoldType.TRI_FOLD_HORIZONTAL:
        return 'icon-tri-fold-h';
      case FoldType.TRI_FOLD_VERTICAL:
        return 'icon-tri-fold-v';
      default:
        throw new Error(`Unhandled page fold dropdown item`);
    }
  };

  const prepareObject = (): ReactElement[] => {
    return MOBILE_FOLD_TYPES_TO_SHOW.map((type) => {
      return (
        <ThumbnailItem
          onClick={(): void => {
            applyFold(type);
          }}
          key={type}
          className={`${styles.items} ${type === FoldType.NONE ? styles.none : ''}`}
          state={ThumbnailItemState.ICON_ONLY}
          isSelected={type === foldType}
          text={getNameFromType(type)}
          textClassName={styles.text}
          thumbnail={<Icon icon={getIconFromType(type)} size={IconSize.SIZE_ICON_32} />}
        />
      );
    });
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="folds-mobile-panel" hasMobileModalPadding contentClassName={`${styles.row} spacing-m-0 flex-h-row`}>
        {prepareObject()}
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}

export default PosterEditingFoldsPanel;
